import { FollowButtonDisplay, FollowButtonOutline, FollowButtonSize } from "@product/scmp-sdk";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import type { articleTopicsContent$key } from "~/queries/__generated__/articleTopicsContent.graphql";

import type { ArticleTopicsContainerProps } from "./styles";
import {
  ArticleTopicsContainer,
  Item,
  Label,
  MainItem,
  StyledEntityFollowButton,
  StyledTopicLink,
  Topics,
  TopicSeparator,
} from "./styles";

export type Props = {
  className?: string;
  label?: string;
  reference: articleTopicsContent$key;
  variant?: ArticleTopicsContainerProps["$variant"];
  withFollow?: boolean;
  withSeparator?: boolean;
};

export const ArticleTopics: FunctionComponent<Props> = ({
  className,
  label,
  reference: reference_,
  variant = "light",
  withFollow = true,
  withSeparator = true,
}) => {
  const { topics } = useFragment(
    graphql`
      fragment articleTopicsContent on Content {
        topics {
          ...topicLinkTopic
          ...entityFollowButtonBase
          entityId
        }
      }
    `,
    reference_,
  );
  if (!topics || topics.length === 0) return null;
  const [mainTopic, ...otherTopics] = topics;

  const renderSeparator = (index: number) => {
    if (index === otherTopics.length - 1) return null;
    return <TopicSeparator>|</TopicSeparator>;
  };

  return (
    <ArticleTopicsContainer $variant={variant} className={className}>
      {label && <Label>{label}</Label>}
      <Topics>
        {mainTopic && (
          <MainItem>
            <StyledTopicLink reference={mainTopic} />
            {withFollow && (
              <StyledEntityFollowButton
                display={FollowButtonDisplay.Icon}
                outline={FollowButtonOutline.Plain}
                reference={mainTopic}
                size={FollowButtonSize.Compact}
                source="Article_topic_footer"
              />
            )}
          </MainItem>
        )}

        {otherTopics.map(
          (topic, index) =>
            topic && (
              <Item key={topic.entityId}>
                <StyledTopicLink reference={topic} />
                {withFollow && (
                  <StyledEntityFollowButton
                    display={FollowButtonDisplay.Icon}
                    outline={FollowButtonOutline.Plain}
                    reference={topic}
                    size={FollowButtonSize.Compact}
                    source="Article_topic_footer"
                  />
                )}
                {withSeparator && renderSeparator(index)}
              </Item>
            ),
        )}
      </Topics>
    </ArticleTopicsContainer>
  );
};

ArticleTopics.displayName = "ArticleTopics";
